import z from 'zod';
import { states } from '../../_sharedData/states';

export type EnglishSpanishText = {
	en: string;
	es: string;
};

export type Question = {
	key: string;
	type: string;
	en: string;
	es: string;
	required: boolean;
	error: EnglishSpanishText;
	placeholder?: EnglishSpanishText;
};

type RadioQuestion = {
	key: string;
	en: string;
	es: string;
	required: boolean;
	error: EnglishSpanishText;
	options: EnglishSpanishText[];
};

export type StepData = {
	questions?: Question[];
	radioQuestions?: RadioQuestion[];
};

export type JsonData = {
	caseInformation: StepData;
	contactInformation: StepData;
	addressInformation: StepData;
	determination: StepData;
	employment: StepData;
	income: StepData;
	assets: StepData;
	debts: StepData;
	expenses: StepData;
	signature: StepData;
};

export type ProgressSteps = EnglishSpanishText[];

export const CaseInformationSchema = z.object({
	defendantName: z.string().min(1).max(50),
	caseNumber: z.string().max(50),
	judge: z.string().min(1).max(50),
});

export type CaseInformationType = z.infer<typeof CaseInformationSchema>;

const phoneRegex = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

export const ContactInformationSchema = z.object({
	firstName: z.string().min(1).max(25),
	middleName: z.string().optional(),
	lastName: z.string().min(1).max(25),
	DOB: z.string().date(),
	phone: z.string().regex(phoneRegex),
	email: z.string().email().max(50),
});

export type ContactInformationType = z.infer<typeof ContactInformationSchema>;

export const stateEnum = z.enum(states);

const zipCodeRegex = /^(\d{5})(-\d{4})?$/;
export const AddressInformationSchema = z.object({
	mailingAddress: z.string().min(1).max(50),
	apartmentNumber: z.string(),
	city: z.string().min(1).max(25),
	state: stateEnum,
	zipCode: z.string().regex(zipCodeRegex),
});

export type AddressInformationType = z.infer<typeof AddressInformationSchema>;

export const maritalStatus = ['Single', 'Married', 'Widowed', 'Divorced'] as const;

export const DeterminationSchema = z
	.object({
		maritalStatus: z.enum(maritalStatus),
		hasDependents: z.boolean(),
		dependents: z
			.array(
				z.object({
					age: z.number().min(0, { message: 'Age must be at least 0' }).nullable(),
					relationship: z.string().min(1, { message: 'Relationship is required' }).nullable(),
				})
			)
			.optional(),
	})
	.refine(
		(data) => {
			if (data.hasDependents) {
				return data.dependents && data.dependents.length > 0;
			}
			return true;
		},
		{
			message: 'If you have dependents, you must provide at least one dependent with their details.',
			path: ['dependents'],
		}
	);

export type DeterminationType = z.infer<typeof DeterminationSchema>;

// Employment options
export const employmentStatus = ['Employed', 'Unemployed', 'Self-Employed'] as const;
export const spouseEmploymentStatusOptions = ['Yes', 'No'] as const;
export const employmentTypes = ['Full-Time', 'Part-Time'] as const;

// Employment Schema with conditional validation
export const EmploymentSchema = z
	.object({
		employmentStatus: z.enum(employmentStatus),
		employmentType: z.enum(employmentTypes).optional(),
		employerName: z.string().max(100).optional(),
		hourlyRate: z.number().int().min(1).optional(),
		hoursWorkedWeekly: z.number().int().min(1).max(168).optional(),
		spouseEmploymentStatus: z.enum(spouseEmploymentStatusOptions).optional(),
		spouseEmploymentType: z.enum(employmentTypes).optional(),
		spouseEmployerName: z.string().max(100).optional(),
		spouseHourlyRate: z.number().int().min(1).optional(),
		spouseHoursWorkedWeekly: z.number().int().min(1).max(168).optional(),
	})
	.refine(
		(data) => {
			return data.employmentStatus !== 'Employed' || data.employmentType !== undefined;
		},
		{
			message: 'Employment type is required when employment status is Employed',
			path: ['employmentType'],
		}
	)
	.refine(
		(data) => {
			return data.employmentStatus !== 'Employed' || data.employerName !== undefined;
		},
		{
			message: 'Employer name is required when employment status is Employed',
			path: ['employerName'],
		}
	)

	.refine(
		(data) => {
			return data.employmentStatus !== 'Employed' || data.hourlyRate !== undefined;
		},
		{
			message: 'Hourly rate is required when employment status is Employed',
			path: ['hourlyRate'],
		}
	)
	.refine(
		(data) => {
			return data.employmentStatus !== 'Employed' || data.hoursWorkedWeekly !== undefined;
		},
		{
			message: 'Hours worked weekly is required when employment status is Employed',
			path: ['hoursWorkedWeekly'],
		}
	)
	.refine(
		(data) => {
			return data.spouseEmploymentStatus !== 'Yes' || data.spouseEmploymentType !== undefined;
		},
		{
			message: 'Spouse employment type is required if spouse is employed',
			path: ['spouseEmploymentType'],
		}
	)
	.refine(
		(data) => {
			return data.spouseEmploymentStatus !== 'Yes' || data.spouseHourlyRate !== undefined;
		},
		{
			message: 'Spouse hourly rate is required if spouse is employed',
			path: ['spouseHourlyRate'],
		}
	)
	.refine(
		(data) => {
			return data.spouseEmploymentStatus !== 'Yes' || data.spouseHoursWorkedWeekly !== undefined;
		},
		{
			message: 'Spouse hours worked weekly is required if spouse is employed',
			path: ['spouseHoursWorkedWeekly'],
		}
	);

// Income Schema with required fields starting as undefined
export const IncomeSchema = z.object({
	retirement: z.number().int().min(0, { message: 'Retirement must be a positive integer' }),
	unemployment: z.number().int().min(0, { message: 'Unemployment must be a positive integer' }),
	alimony: z.number().int().min(0, { message: 'Alimony must be a positive integer' }),
	childSupport: z.number().int().min(0, { message: 'Child Support must be a positive integer' }),
	ssiDisability: z.number().int().min(0, { message: 'SSI Disability must be a positive integer' }),
	publicAssistance: z.number().int().min(0, { message: 'Public Assistance must be a positive integer' }),
	otherIncome: z.number().int().min(0, { message: 'Other Income must be a positive integer' }),
});

// Combined Employment and Income Schema
export const EmploymentIncomeSchema = z.object({
	employment: EmploymentSchema,
	income: IncomeSchema,
	userEnteredIncome: z.number().int().min(0, { message: 'Please enter enter a postiive number' }),
	explanation: z.string().optional(),
});

// Type definitions
export type EmploymentType = z.infer<typeof EmploymentSchema>;
export type IncomeType = z.infer<typeof IncomeSchema>;
export type EmploymentIncomeType = z.infer<typeof EmploymentIncomeSchema>;

export const AssetsSchema = z.object({
	cashValue: z.number().int().min(0, { message: 'Cash value must be a positive integer' }),
	propertyValue: z.number().int().min(0, { message: 'Property value must be a positive integer' }),
	vehicleValue: z.number().int().min(0, { message: 'Vehicle value must be a positive integer' }),
	makeAndModel: z.string().optional(),
	retirementValue: z.number().int().min(0, { message: 'Retirement value must be a positive integer' }),
	stockValue: z.number().int().min(0, { message: 'Stock value must be a positive integer' }),
	otherItemsValue: z.number().int().min(0, { message: 'Other items value must be a positive integer' }),
	totalAssets: z.number().int().min(0, { message: 'Total assets must be a positive integer' }),
});

export type AssetsType = z.infer<typeof AssetsSchema>;

// Adjusting the schema to conditionally validate based on hasDebts
export const DebtsSchema = z
	.object({
		hasDebts: z.boolean(),
		totalAmountOwed: z.number().int(),
		debts: z
			.array(
				z.object({
					amountOwed: z.number().int().min(1, { message: 'Total owed must be greater than 0' }),
					monthlyPayment: z.number().int().min(1, { message: 'Monthly payment must be greater than 0' }),
					collateral: z.string().optional(),
				})
			)
			.default([]), // Set default to an empty array
	})
	.refine(
		(data) => {
			if (data.hasDebts) {
				return data.debts.length > 0;
			}
			return true;
		},
		{
			message: 'You must add at least one debt or click the X to remove all debts from the list',
			path: ['debts'],
		}
	);

export type DebtsType = z.infer<typeof DebtsSchema>;

export const ExpensesSchema = z.object({
	housing: z.number().int().min(0, { message: 'Housing value must be a positive integer' }),
	food: z.number().int().min(0, { message: 'Food value must be a positive integer' }),
	utilities: z.number().int().min(0, { message: 'Utilities value must be a positive integer' }),
	transportation: z.number().int().min(0, { message: 'Transportation value must be a positive integer' }),
	medical: z.number().int().min(0, { message: 'Medical value must be a positive integer' }),
	school: z.number().int().min(0, { message: 'School value must be a positive integer' }),
	clothing: z.number().int().min(0, { message: 'Clothing items value must be a positive integer' }),
	childSupport: z.number().int().min(0, { message: 'Child Support value must be a positive integer' }),
	alimony: z.number().int().min(0, { message: 'Alimony must be a positive integer' }),
	other: z.number().int().min(0, { message: 'Other value must be a positive integer' }),
	attorneyFees: z.number().int().min(0, { message: 'Attorney Fees value must be a positive integer' }),
	necessities: z.number().int().min(0, { message: 'Necessities value must be a positive integer' }),
	totalExpenses: z.number().int().min(0, { message: 'Total Expenses value must be a positive integer' }),
});

export type ExpensesType = z.infer<typeof ExpensesSchema>;

export const SignatureSchema = z.object({
	userExplanation: z.string().optional(),
	locationSignedAt: z.string().min(1),
	dateSigned: z.string().datetime(),
	defendantSignature: z.string().min(1, { message: 'Please enter your full name' }).max(50),
});

export type SignatureType = z.infer<typeof SignatureSchema>;

export const FinancialStatusFormSubmissionSchema = z.object({
	lang: z.enum(['en', 'es']),
	caseInformation: CaseInformationSchema,
	contactInformation: ContactInformationSchema,
	addressInformation: AddressInformationSchema,
	determination: DeterminationSchema,
	employmentIncome: EmploymentIncomeSchema,
	assets: AssetsSchema,
	debts: DebtsSchema,
	expenses: ExpensesSchema,
	signature: SignatureSchema,
});

export type FinancialStatusFormSubmissionType = z.infer<typeof FinancialStatusFormSubmissionSchema>;
